import type { ReactNode } from 'react';
import React, { memo } from 'react';

import { Box, List, ListItem, ListItemText } from '@mui/material';

import { Permission } from '@/entities/permissions';
import { DocumentationInfo, TokenLink } from '@/features/DataHub/modules/Export/components/Shared';
import { useLatestApiKey } from '@/features/DataHub/modules/Export/hooks';
import type { ExportOption } from '@/features/DataHub/modules/Export/types';
import usePermission from '@/shared/hooks/usePermission';

import type { SxProps, Theme } from '@mui/material/styles';

const containerSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };

const listSx: SxProps<Theme> = theme => ({
  p: 0,
  ml: 1,
  '.MuiListItemText-root': {
    m: 0
  },
  '.MuiTypography-root': {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '28px'
  },
  '.MuiListItem-root': { p: 0, alignItems: 'center' },
  '.MuiButtonBase-root': {
    m: 0,
    fontSize: theme.typography.pxToRem(14)
  }
});

const bulletSx: SxProps<Theme> = { backgroundColor: 'text.primary', width: 4, height: 4, mr: 1 };

type PreviewScreenMeta = Pick<ExportOption, 'showTokenLink' | 'bulletPoints' | 'documentationType'>;

const ExportPreviewScreen = memo(function ExportPreviewScreen({
  previewMeta: { showTokenLink, bulletPoints, documentationType },
  children
}: {
  previewMeta: PreviewScreenMeta;
  children?: ReactNode;
}) {
  const { hasPermission } = usePermission();
  const apiKey = useLatestApiKey();

  const content = hasPermission(Permission.ExportBasic) ? children : null;

  return (
    <Box sx={containerSx}>
      {bulletPoints && bulletPoints?.length > 0 && (
        <List sx={listSx}>
          {bulletPoints?.map((option: string) => (
            <ListItem key={option}>
              <Box sx={bulletSx} />
              <ListItemText primary={option} />
            </ListItem>
          ))}
        </List>
      )}
      {showTokenLink && !apiKey ? <TokenLink /> : undefined}
      {documentationType && <DocumentationInfo type={documentationType} />}
      {content}
    </Box>
  );
});

export default ExportPreviewScreen;
